import { supabase } from '../utils/supabase';
import { normalizeEmail } from './guestAccountLinkingService';

export const PENDING_REGISTRATION_STORAGE_KEY = 'pendingRegistrationData';

/**
 * Resolve first name, last name, and gender from auth user_metadata and optional localStorage backup.
 * @param {import('@supabase/supabase-js').User} authUser
 * @returns {{ first_name: string|null, last_name: string|null, gender: string|null }}
 */
export const resolveRegistrationProfile = (authUser) => {
  const meta = authUser?.user_metadata || {};
  let fromStorage = {};

  try {
    const stored = localStorage.getItem(PENDING_REGISTRATION_STORAGE_KEY);
    if (stored) {
      fromStorage = JSON.parse(stored);
    }
  } catch (e) {
    console.error('[AUTH] Error parsing pending registration data:', e);
  }

  const first_name =
    meta.first_name ||
    meta.firstName ||
    fromStorage.first_name ||
    null;
  const last_name =
    meta.last_name ||
    meta.lastName ||
    fromStorage.last_name ||
    null;
  const gender =
    meta.gender ||
    fromStorage.gender ||
    null;

  return { first_name, last_name, gender };
};

export const clearPendingRegistrationData = () => {
  localStorage.removeItem(PENDING_REGISTRATION_STORAGE_KEY);
  localStorage.removeItem('pendingVerificationEmail');
};

/**
 * Create a players row for an authenticated user when one does not exist yet.
 * Profile fields must come from user_metadata (sign-up) or pendingRegistrationData (localStorage).
 * @param {import('@supabase/supabase-js').User} authUser
 * @returns {Promise<{ created: boolean, existed: boolean, missingProfile?: boolean }>}
 */
export const ensurePlayerRecord = async (authUser) => {
  if (!authUser?.id) {
    throw new Error('Cannot create player record without auth user id');
  }

  const { data: existingPlayers, error: lookupError } = await supabase
    .from('players')
    .select('id')
    .eq('auth_id', authUser.id);

  if (lookupError) {
    throw lookupError;
  }

  if (existingPlayers && existingPlayers.length > 0) {
    return { created: false, existed: true };
  }

  const profile = resolveRegistrationProfile(authUser);

  if (!profile.first_name || !profile.last_name || !profile.gender) {
    console.warn('[AUTH] Missing registration profile for new player', {
      authId: authUser.id,
      hasMetadata: Boolean(authUser.user_metadata?.first_name),
    });
    return { created: false, existed: false, missingProfile: true };
  }

  const { error: createError } = await supabase.from('players').insert([
    {
      auth_id: authUser.id,
      email: (authUser.email || '').toLowerCase().trim(),
      first_name: profile.first_name,
      last_name: profile.last_name,
      gender: profile.gender,
      role: 'member',
      is_active: true,
    },
  ]);

  if (createError) {
    throw createError;
  }

  clearPendingRegistrationData();
  return { created: true, existed: false };
};

/**
 * Resolve first and last name from Google OAuth user_metadata.
 * Google may supply given_name/family_name or only full_name.
 * Gender is intentionally not mapped — it must be chosen by the user.
 *
 * @param {import('@supabase/supabase-js').User} authUser
 * @returns {{ first_name: string|null, last_name: string|null }}
 */
export const resolveGoogleProfile = (authUser) => {
  const meta = authUser?.user_metadata || {};

  const first_name =
    meta.given_name ||
    meta.first_name ||
    meta.firstName ||
    (meta.full_name ? meta.full_name.split(' ')[0] : null) ||
    null;

  const last_name =
    meta.family_name ||
    meta.last_name ||
    meta.lastName ||
    (meta.full_name && meta.full_name.includes(' ')
      ? meta.full_name.split(' ').slice(1).join(' ')
      : null) ||
    null;

  return { first_name, last_name };
};

/**
 * Create a players row from an explicitly provided profile payload.
 * Used by the /auth/complete-profile page for OAuth users.
 *
 * Unlike ensurePlayerRecord, this function accepts the full profile
 * directly so it is not blocked by missing/incomplete user_metadata.
 *
 * @param {import('@supabase/supabase-js').User} authUser
 * @param {{ first_name: string, last_name: string, gender: string }} profile
 * @returns {Promise<{ created: boolean, existed: boolean, duplicateEmail?: boolean }>}
 */
export const createPlayerRecordFromProfile = async (authUser, profile) => {
  if (!authUser?.id) {
    throw new Error('Cannot create player record without auth user id');
  }

  // Check for existing row first (idempotent).
  const { data: existing, error: lookupError } = await supabase
    .from('players')
    .select('id')
    .eq('auth_id', authUser.id)
    .limit(1);

  if (lookupError) throw lookupError;
  if (existing && existing.length > 0) return { created: false, existed: true };

  // Normalize email consistent with guestAccountLinkingService comparisons.
  const email = normalizeEmail(authUser.email || '') || (authUser.email || '').toLowerCase().trim();

  const { error: insertError } = await supabase.from('players').insert([
    {
      auth_id: authUser.id,
      email,
      first_name: profile.first_name,
      last_name: profile.last_name,
      gender: profile.gender,
      role: 'member',
      is_active: true,
    },
  ]);

  if (insertError) {
    if (insertError.code === '23505') {
      // UNIQUE violation — either auth_id (shouldn't happen after the check above)
      // or email (another auth user already has this email in players).
      console.error('[AUTH] profile_insert_23505', {
        code: insertError.code,
        detail: insertError.details,
      });
      return { created: false, existed: false, duplicateEmail: true };
    }
    throw insertError;
  }

  return { created: true, existed: false };
};
